import { times } from 'lodash-es';
import * as React from 'react';
import { useIntl } from 'react-intl';

import * as Styled from './pagination-dots.styles';
import type {
  DotProps,
  PaginationDotProps,
  DotSize,
} from './pagination-dots.types';

const MAX_DOTS_VISIBLE = 7;
const CENTER_INDEX = Math.floor(MAX_DOTS_VISIBLE / 2);

const Dot: React.FC<DotProps> = ({
  isSelected,
  paginationDotColor,
  position,
  size,
  total,
}) => {
  const intl = useIntl();

  const ariaLabel = intl.formatMessage(
    {
      id: isSelected
        ? 'XDS_PAGINATION_DOTS_ITEM_LABEL_CURRENT'
        : 'XDS_PAGINATION_DOTS_SLIDE_LABEL',
      defaultMessage: isSelected
        ? 'Current item, {position} of {total}'
        : '{position} of {total}',
    },
    { position, total }
  );

  return (
    <Styled.DotContainer>
      <Styled.Dot
        $isSelected={isSelected}
        $paginationDotColor={paginationDotColor}
        $size={size}
        aria-current={isSelected ? 'true' : 'false'}
        aria-label={ariaLabel}
        role="status"
      />
    </Styled.DotContainer>
  );
};

export const PaginationDots = React.forwardRef<
  HTMLDivElement,
  PaginationDotProps
>(
  (
    {
      'aria-label': providedAriaLabel,
      className,
      length,
      paginationDotColor,
      selected,
      ...props
    },
    forwardedRef
  ) => {
    const intl = useIntl();
    const statusId = React.useId();

    const ariaLabel = React.useMemo(() => {
      return (
        providedAriaLabel ||
        intl.formatMessage({
          id: 'XDS_PAGINATION_DOTS_LABEL',
          defaultMessage: 'Pagination',
        })
      );
    }, [providedAriaLabel, intl]);

    const statusText = React.useMemo(() => {
      return intl.formatMessage(
        {
          id: 'XDS_PAGINATION_DOTS_ITEM_LABEL',
          defaultMessage: '{position} of {total}',
        },
        { position: selected + 1, total: length }
      );
    }, [intl, selected, length]);

    const getDotType = (index: number): DotSize => {
      if (length < 5) {
        return 'big';
      }

      const diff = Math.abs(index - selected);

      switch (diff) {
        case 0:
          return 'big';
        case 1:
          return 'big';
        case 2:
          return 'medium';
        default:
          return 'small';
      }
    };

    if (length < 2) {
      return <Styled.PaginationDots />;
    }

    const getTrackPosition = (): number => {
      if (length <= MAX_DOTS_VISIBLE) return 0;

      if (selected < CENTER_INDEX) return 0;
      if (selected >= length - CENTER_INDEX) return length - MAX_DOTS_VISIBLE;
      return selected - CENTER_INDEX;
    };

    return (
      <Styled.PaginationDots
        aria-describedby={statusId}
        aria-label={ariaLabel}
        className={className}
        data-xds="PaginationDots"
        ref={forwardedRef}
        role="group"
        {...props}
      >
        {/* Hidden status for screen readers */}
        <Styled.VisuallyHidden
          aria-atomic="true"
          aria-live="polite"
          id={statusId}
        >
          {statusText}
        </Styled.VisuallyHidden>
        <Styled.Track
          $centered={length < MAX_DOTS_VISIBLE}
          $index={getTrackPosition()}
        >
          {times(length, (i: number) => (
            <Dot
              isSelected={selected === i}
              key={i}
              paginationDotColor={paginationDotColor}
              position={i + 1}
              size={getDotType(i)}
              total={length}
            />
          ))}
        </Styled.Track>
      </Styled.PaginationDots>
    );
  }
);

PaginationDots.displayName = 'PaginationDots';
